import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  Archive,
  BarChart3,
  CalendarDays,
  DatabaseBackup,
  FileBarChart,
  History,
  ShieldCheck,
  Stethoscope,
  Upload,
  UserCog,
  Users,
  Trash2,
  User,
} from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NAV_ITEMS = [
  { label: "Dashboard", to: "/", icon: Activity },
  { label: "Patients", to: "/patients", icon: Users },
  { label: "Program", to: "/program", icon: CalendarDays },
  { label: "Reports", to: "/reports", icon: FileBarChart },
  { label: "Auth Alerts", to: "/authorisations", icon: ShieldCheck },
  { label: "Transplant Load", to: "/transplant-load", icon: BarChart3 },
  { label: "Backlog", to: "/backlog", icon: Archive },
  { label: "Follow-ups", to: "/followups", icon: Stethoscope },
];

const ADMIN_ITEMS = [
  { label: "Users", to: "/admin/users", icon: UserCog },
  { label: "Import XLSX", to: "/admin/import", icon: Upload },
  { label: "Audit Log", to: "/admin/audit", icon: History },
  { label: "Recycle Bin", to: "/admin/recycle-bin", icon: Trash2 },
  { label: "Backups", to: "/admin/backups", icon: DatabaseBackup },
];

export default function CommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [patients, setPatients] = useState([]);
  const [searching, setSearching] = useState(false);

  // Debounced patient search
  useEffect(() => {
    if (!open) {
      setQuery("");
      setPatients([]);
      return;
    }
    if (query.length < 2) {
      setPatients([]);
      return;
    }
    setSearching(true);
    const t = setTimeout(() => {
      api
        .get("/patients", { params: { q: query } })
        .then((r) => setPatients((r.data || []).slice(0, 8)))
        .catch(() => setPatients([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [query, open]);

  // Global Ctrl+K / Cmd+K shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        onOpenChange(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onOpenChange]);

  const go = useCallback(
    (to) => {
      onOpenChange(false);
      navigate(to);
    },
    [navigate, onOpenChange],
  );

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search patients or navigate…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>
          {searching ? "Searching…" : "No results found."}
        </CommandEmpty>

        {patients.length > 0 && (
          <>
            <CommandGroup heading="Patients">
              {patients.map((p) => (
                <CommandItem
                  key={p.id}
                  value={`${p.name} ${p.patient_code}`}
                  onSelect={() => go(`/patients/${p.id}`)}
                  className="gap-3"
                >
                  <User className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="font-medium">{p.name}</span>
                  <span className="text-muted-foreground text-xs">{p.patient_code}</span>
                  {p.current_status && (
                    <span className="ml-auto text-xs text-muted-foreground">{p.current_status}</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        <CommandGroup heading="Navigate">
          {NAV_ITEMS.map((item) => (
            <CommandItem
              key={item.to}
              value={item.label}
              onSelect={() => go(item.to)}
              className="gap-3"
            >
              <item.icon className="w-4 h-4 text-muted-foreground shrink-0" />
              {item.label}
            </CommandItem>
          ))}
        </CommandGroup>

        {user?.role === "admin" && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Admin">
              {ADMIN_ITEMS.map((item) => (
                <CommandItem
                  key={item.to}
                  value={`admin ${item.label}`}
                  onSelect={() => go(item.to)}
                  className="gap-3"
                >
                  <item.icon className="w-4 h-4 text-muted-foreground shrink-0" />
                  {item.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
